import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Client, Site } from '../../interfaces/tenancy';
import { tenancyService } from '../services/tenancy.service';
import { Envelope } from '../../interfaces/envelope';

interface TenancyState {
	clients: {
		[id: number]: Client;
	};
	sites: {
		[id: number]: Site;
	};
	selectedClient: Client | undefined;
	selectedSite: Site | undefined;
	isLoading: boolean;
}

const initialState = {
	clients: [],
	sites: [],
	selectedClient: undefined,
	selectedSite: undefined,
	isLoading: false,
} as TenancyState;

export const fetchClients = createAsyncThunk<
	Client[],
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('authentication/fetchClients', async (_undefined, { rejectWithValue }) => {
	try {
		return await tenancyService.fetchClients();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchSites = createAsyncThunk<
	Site[],
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('authentication/fetchSites', async (_undefined, { rejectWithValue }) => {
	try {
		return await tenancyService.fetchSites();
	} catch (error) {
		return rejectWithValue(error);
	}
});

const tenancySlice = createSlice({
	name: 'tenancy',
	initialState,
	reducers: {
		selectClient: (state, action: PayloadAction<number>) => {
			state.selectedClient = state.clients[action.payload];
		},
		deSelectClient: (state) => {
			state.selectedClient = undefined;
		},
		selectSite: (state, action: PayloadAction<number>) => {
			state.selectedSite = state.sites[action.payload];
		},
		deSelectSite: (state) => {
			state.selectedSite = undefined;
		},
		resetTenancy: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchClients.fulfilled, (state, action) => {
				const ret: { [id: number]: Client } = {};
				state.clients = action.payload.reduce((obj, client) => {
					obj[client.id] = client;
					return obj;
				}, ret);
				state.isLoading = false;
			})
			.addCase(fetchClients.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchClients.rejected, (state, action) => {
				state.clients = [];
				state.isLoading = false;
			})
			.addCase(fetchSites.fulfilled, (state, action) => {
				const ret: { [id: number]: Site } = {};
				state.sites = action.payload.reduce((obj, site) => {
					obj[site.id] = site;
					return obj;
				}, ret);
				state.isLoading = false;
			})
			.addCase(fetchSites.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchSites.rejected, (state, action) => {
				state.sites = [];
				state.isLoading = false;
			});
	},
});

export const { selectClient, deSelectClient, selectSite, deSelectSite, resetTenancy } =
	tenancySlice.actions;

export default tenancySlice;
